import { useState, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'

interface NewsItem {
  id: string
  headline: string
  source: string
  url: string
  summary: string
  datetime: number
  image: string | null
  category: string
}

interface Props {
  ticker?: string
  compact?: boolean
  maxRows?: number
}

const CATEGORY_COLORS: Record<string, string> = {
  general: 'var(--cash)',
  forex:   'var(--amber)',
  crypto:  '#a78bfa',
  merger:  '#60a5fa',
  company: 'var(--cash)',
}

const CATEGORY_LABELS: Record<string, string> = {
  general: 'MKT',
  forex:   'FX',
  crypto:  'CRYPTO',
  merger:  'M&A',
  company: 'CO',
}

const ALL_CATEGORIES = ['all', 'general', 'forex', 'crypto', 'merger']

export default function NewsTicker({ ticker, compact = false, maxRows = 14 }: Props) {
  const [visibleItems, setVisibleItems] = useState<(NewsItem & { key: string; isNew: boolean })[]>([])
  const [filter,    setFilter]    = useState('')
  const [paused,    setPaused]    = useState(false)
  const [expanded,  setExpanded]  = useState<string | null>(null)
  const [activeCat, setActiveCat] = useState('all')
  const streamIdxRef = useRef(0)
  const intervalRef  = useRef<number | undefined>(undefined)
  const itemsRef     = useRef<NewsItem[]>([])   // live category items — read by the streaming interval (avoids stale closure). Code review P2.

  const { data, isLoading, error } = useQuery({
    queryKey: ticker ? ['news-ticker', ticker] : ['news-market'],
    queryFn:  ticker ? () => api.news.ticker(ticker) : () => api.news.market(),
    staleTime: 15 * 60 * 1000,
    refetchInterval: 15 * 60 * 1000,
  })

  const allItems: NewsItem[] = Array.isArray(data) ? data : []

  // Filter by active category
  const categoryItems = activeCat === 'all'
    ? allItems
    : allItems.filter(i => i.category === activeCat)

  const timeAgo = (ts: number) => {
    const mins = Math.floor((Date.now() / 1000 - ts) / 60)
    if (mins < 1)    return 'now'
    if (mins < 60)   return `${mins}m`
    if (mins < 1440) return `${Math.floor(mins / 60)}h`
    return `${Math.floor(mins / 1440)}d`
  }

  // Keep the ref pointed at the latest items so the interval always streams fresh data (not a stale
  // closure snapshot from when the effect last ran). Code review P2.
  itemsRef.current = categoryItems

  // Stream items one by one
  useEffect(() => {
    if (categoryItems.length === 0 || paused) return
    if (intervalRef.current) clearInterval(intervalRef.current)
    streamIdxRef.current = 0

    intervalRef.current = window.setInterval(() => {
      const items = itemsRef.current
      if (items.length === 0) return
      const idx  = streamIdxRef.current % items.length
      const item = items[idx]
      streamIdxRef.current++

      setVisibleItems(prev => {
        const updated = prev.map(i => ({ ...i, isNew: false }))
        const next    = [{ ...item, key: `${item.id}-${Date.now()}`, isNew: true }, ...updated]
        return next.slice(0, maxRows)
      })
    }, 3500)

    return () => { if (intervalRef.current) clearInterval(intervalRef.current) }
  }, [categoryItems.length, activeCat, paused, maxRows])

  // Seed initial visible items when data loads or category changes
  useEffect(() => {
    if (categoryItems.length === 0) return
    const initial = categoryItems.slice(0, Math.min(8, categoryItems.length)).map((item, i) => ({
      ...item,
      key:   `${item.id}-init-${i}`,
      isNew: false,
    }))
    setVisibleItems(initial)
    streamIdxRef.current = initial.length
  }, [allItems.length, activeCat])

  const filtered = filter
    ? visibleItems.filter(i =>
        i.headline.toLowerCase().includes(filter.toLowerCase()) ||
        i.source.toLowerCase().includes(filter.toLowerCase()) ||
        i.category.toLowerCase().includes(filter.toLowerCase())
      )
    : visibleItems

  // ── Category pill ──────────────────────────────────────────────────────────
  function CatBadge({ cat }: { cat: string }) {
    const color = CATEGORY_COLORS[cat] ?? 'var(--muted)'
    const label = CATEGORY_LABELS[cat] ?? cat.toUpperCase()
    return (
      <span style={{
        fontFamily: 'Syne Mono', fontSize: '.48rem', padding: '1px 5px',
        border: `1px solid ${color}`, borderRadius: 2,
        color, opacity: .85, flexShrink: 0, lineHeight: 1.6,
      }}>{label}</span>
    )
  }

  // ── Compact scrolling bar ──────────────────────────────────────────────────
  if (compact) {
    const current = visibleItems[0]
    return (
      <div style={{
        background: 'var(--paper)', borderBottom: '1px solid var(--edge)',
        height: 30, display: 'flex', alignItems: 'center', overflow: 'hidden', flexShrink: 0,
      }}>
        <div style={{
          fontFamily: 'Syne Mono', fontSize: '.56rem', color: 'var(--cash)',
          padding: '0 12px', borderRight: '1px solid var(--edge)',
          height: '100%', display: 'flex', alignItems: 'center',
          flexShrink: 0, letterSpacing: '.14em', background: 'var(--stamp)', whiteSpace: 'nowrap',
        }}>
          {ticker ?? 'MARKETS'}
        </div>
        <div style={{ overflow: 'hidden', flex: 1, position: 'relative', height: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '0 12px' }}>
          {isLoading && (
            <span style={{ fontFamily: 'Syne Mono', fontSize: '.56rem', color: 'var(--muted)' }}>Loading news...</span>
          )}
          {!isLoading && current && (
            <>
              <span style={{
                fontFamily: 'Syne Mono', fontSize: '.46rem', padding: '1px 4px',
                border: `1px solid ${CATEGORY_COLORS[current.category] ?? 'var(--edge)'}`,
                color: CATEGORY_COLORS[current.category] ?? 'var(--muted)',
                borderRadius: 2, flexShrink: 0,
              }}>
                {CATEGORY_LABELS[current.category] ?? current.category.toUpperCase()}
              </span>
              <div
                key={current.key}
                onClick={() => window.open(current.url, '_blank', 'noopener')}
                style={{
                  fontFamily: 'Syne Mono', fontSize: '.56rem', whiteSpace: 'nowrap',
                  overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer', flex: 1,
                  animation: 'tickIn .3s ease',
                }}
              >
                <span style={{ color: 'var(--muted)', marginRight: 8 }}>{current.source}</span>
                <span style={{ color: 'var(--text)' }}>{current.headline}</span>
              </div>
            </>
          )}
        </div>
        <style>{`@keyframes tickIn { from { opacity:0; transform:translateY(-8px) } to { opacity:1; transform:translateY(0) } }`}</style>
      </div>
    )
  }

  // ── Full terminal feed ─────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>

      {/* Terminal header */}
      <div style={{
        background: '#0d0d0d', border: '1px solid var(--edge)',
        borderBottom: 'none', borderRadius: '4px 4px 0 0',
        padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', gap: 5, marginRight: 4 }}>
          {['#e05c5c','#d4a843','#85c47c'].map(col => (
            <div key={col} style={{ width: 9, height: 9, borderRadius: '50%', background: col, opacity: .7 }} />
          ))}
        </div>

        <div style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--muted)', letterSpacing: '.14em' }}>
          {ticker ? `${ticker} — company news` : 'market news feed'} {paused ? '· PAUSED' : '· LIVE'}
          {!isLoading && <span style={{ color: 'var(--cash)', marginLeft: 8 }}>● {allItems.length} items</span>}
        </div>

        <div style={{ flex: 1 }} />

        <input
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder="filter..."
          style={{
            background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2,
            padding: '3px 8px', color: 'var(--text)', fontFamily: 'Syne Mono',
            fontSize: '.6rem', outline: 'none', width: 90,
          }}
        />

        <button
          onClick={() => setPaused(p => !p)}
          style={{
            background: paused ? 'var(--stamp)' : 'transparent',
            border: `1px solid ${paused ? 'var(--cash-dim)' : 'var(--edge)'}`,
            borderRadius: 2, padding: '3px 10px', cursor: 'pointer',
            fontFamily: 'Syne Mono', fontSize: '.58rem',
            color: paused ? 'var(--cash)' : 'var(--muted)',
          }}
        >
          {paused ? '▶ resume' : '⏸ pause'}
        </button>
      </div>

      {/* Category filter tabs — only show for market feed, not ticker */}
      {!ticker && (
        <div style={{
          background: '#0d0d0d', borderLeft: '1px solid var(--edge)', borderRight: '1px solid var(--edge)',
          padding: '6px 12px', display: 'flex', gap: 5, flexWrap: 'wrap',
          borderBottom: '1px solid var(--edge)',
        }}>
          {ALL_CATEGORIES.map(cat => {
            const isActive = activeCat === cat
            const color    = cat === 'all' ? 'var(--cash)' : (CATEGORY_COLORS[cat] ?? 'var(--muted)')
            const count    = cat === 'all' ? allItems.length : allItems.filter(i => i.category === cat).length
            return (
              <button key={cat} onClick={() => setActiveCat(cat)} style={{
                background: isActive ? 'rgba(255,255,255,.05)' : 'transparent',
                border: `1px solid ${isActive ? color : 'var(--edge)'}`,
                borderRadius: 2, padding: '3px 9px', cursor: 'pointer',
                fontFamily: 'Syne Mono', fontSize: '.56rem',
                color: isActive ? color : 'var(--muted)',
                transition: 'all .1s',
                display: 'flex', alignItems: 'center', gap: 5,
              }}>
                {cat === 'all' ? 'ALL' : (CATEGORY_LABELS[cat] ?? cat.toUpperCase())}
                <span style={{ opacity: .5, fontSize: '.48rem' }}>{count}</span>
              </button>
            )
          })}
        </div>
      )}

      {/* Feed window */}
      <div style={{
        background: '#0a0a0a', border: '1px solid var(--edge)',
        borderTop: 'none',
        borderRadius: ticker ? '0 0 4px 4px' : '0 0 4px 4px',
        fontFamily: 'Syne Mono', fontSize: '.64rem',
        height: 360, overflowY: 'auto', overflowX: 'hidden',
        display: 'flex', flexDirection: 'column',
        padding: '4px 0',
      }}>
        {isLoading && (
          <div style={{ padding: '12px 14px', color: 'var(--muted)', display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ color: 'var(--cash)', animation: 'blink 1s step-end infinite' }}>█</span>
            Connecting to Finnhub...
          </div>
        )}
        {error && (
          <div style={{ padding: '12px 14px', color: 'var(--red)' }}>
            ✗ Failed to load — check FINNHUB_API_KEY
          </div>
        )}
        {!isLoading && !error && filtered.length === 0 && (
          <div style={{ padding: '12px 14px', color: 'var(--muted)' }}>
            {filter ? 'No matches' : 'Waiting for news stream...'}
          </div>
        )}

        {filtered.map(item => {
          const isExp = expanded === item.key
          return (
            <div key={item.key}
              style={{
                padding: '5px 14px',
                borderBottom: '1px solid rgba(42,42,42,0.6)',
                background: item.isNew ? 'rgba(133,196,124,0.04)' : 'transparent',
                animation: item.isNew ? 'rowIn .25s ease' : 'none',
                transition: 'background .8s',
                cursor: 'pointer',
              }}
              onClick={() => setExpanded(isExp ? null : item.key)}
            >
              <div style={{ display: 'flex', gap: 7, alignItems: 'baseline', lineHeight: 1.5 }}>
                <span style={{ color: 'var(--muted)', flexShrink: 0, fontSize: '.52rem', minWidth: 22 }}>
                  {timeAgo(item.datetime)}
                </span>
                <CatBadge cat={item.category} />
                <span style={{
                  color: 'var(--cash)', flexShrink: 0, minWidth: 60,
                  fontSize: '.54rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  [{item.source}]
                </span>
                <span style={{
                  color: item.isNew ? 'var(--bill)' : 'var(--text)',
                  flex: 1, lineHeight: 1.4,
                  whiteSpace: isExp ? 'normal' : 'nowrap',
                  overflow: isExp ? 'visible' : 'hidden',
                  textOverflow: isExp ? 'clip' : 'ellipsis',
                }}>
                  {item.headline}
                </span>
                <span style={{ color: 'var(--edge)', flexShrink: 0, fontSize: '.5rem' }}>
                  {isExp ? '▲' : '▼'}
                </span>
              </div>

              {isExp && (
                <div style={{
                  marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--edge)',
                  color: 'var(--muted)', fontSize: '.6rem', lineHeight: 1.7,
                }}>
                  {(item.summary || 'No summary available.').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()}
                  <a href={item.url} target="_blank" rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    style={{
                      display: 'inline-block', marginTop: 6,
                      color: 'var(--cash)', fontFamily: 'Syne Mono',
                      fontSize: '.56rem', textDecoration: 'none',
                    }}
                  >
                    Read full article →
                  </a>
                </div>
              )}
            </div>
          )
        })}

        <style>{`
          @keyframes rowIn { from { opacity:0; background:rgba(133,196,124,0.1); transform:translateY(-4px) } to { opacity:1; transform:translateY(0) } }
          @keyframes blink  { 0%,100%{opacity:1} 50%{opacity:0} }
        `}</style>
      </div>

      <div style={{ fontFamily: 'Syne Mono', fontSize: '.52rem', color: 'var(--muted)', opacity: .4, marginTop: 5, textAlign: 'right' }}>
        Finnhub · general · forex · crypto · M&A · cached 15min
      </div>
    </div>
  )
}
